import api from './api';
import { getStoreIdFromPath } from '../utils/storeUtils';

const StoreService = {
  // Get all stores for the current user
  getStores: async () => {
    const response = await api.get('/stores/basic-details');
    return response.data;
  },

  // Create a new store
  createStore: async (storeData) => {
    const response = await api.post('/stores', storeData);
    return response.data;
  },

  getStoreCreationEligibility: async () => {
    const response = await api.get('/stores/creation-eligibility');
    return response.data;
  },

  registerStoreOwner: async (registrationData) => {
    const response = await api.post('/stores/self-register', registrationData);
    return response.data;
  },

  updateStore: async (storeId, storeData) => {
    const response = await api.put(`/stores/${storeId}`, storeData);
    return response.data;
  },

  checkSlugAvailability: async ({ slug } = {}) => {
    const storeIdFromPath = typeof window !== 'undefined'
      ? getStoreIdFromPath(window.location.pathname)
      : null;
    const response = await api.get('/stores/slug-availability', {
      params: { slug },
      ...(!storeIdFromPath ? { _omitStoreId: true } : {}),
    });
    return response.data;
  },

  updateAuthorizationHours: async (storeId, authorizationHours) => {
    const response = await api.put(`/stores/${storeId}/authorization-hours`, { authorizationHours });
    return response.data;
  },

  updateTimePricing: async (storeId, timePricing) => {
    const response = await api.put(`/stores/${storeId}/time-pricing`, timePricing);
    return response.data;
  },

  // Get store by ID
  getStore: async (storeId) => {
    const response = await api.get(`/stores/${storeId}`);
    return response.data;
  },

  // Add images to store
  addImages: async (storeId, images, category) => {
    const formData = new FormData();
    images.forEach((image, index) => {
      formData.append('files', image.file);
    });
    
    const response = await api.post(`/stores/${storeId}/images?category=${category}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete store image
  deleteImage: async (storeId, imageId) => {
    const response = await api.delete(`/stores/${storeId}/images/${imageId}`);
    return response.data;
  },

  // Update store status (enable/disable)
  updateStoreStatus: async (storeId, disabled) => {
    const response = await api.patch(`/stores/${storeId}/status`, { disabled });
    return response.data;
  },

  getPaymentSettings: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/payment-settings`, { _storeId: storeId });
    return response.data;
  },

  // Leaving servicePackageCustomerPaymentMode out keeps the store's current package mode.
  updatePaymentSettings: async (storeId, {
    appointmentCustomerPaymentMode,
    membershipCustomerPaymentMode,
    servicePackageCustomerPaymentMode,
    enabledPaymentMethodTypes,
  }) => {
    const response = await api.patch(`/stores/${storeId}/payment-settings`, {
      appointmentCustomerPaymentMode,
      membershipCustomerPaymentMode,
      ...(servicePackageCustomerPaymentMode != null ? { servicePackageCustomerPaymentMode } : {}),
      enabledPaymentMethodTypes,
    });
    return response.data;
  },

  // The name customers see on their bank statement for this store's online charges.
  getStatementDescriptor: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/statement-descriptor`, { _storeId: storeId });
    const data = response.data;
    return {
      statementDescriptor: data?.statementDescriptor ?? data?.StatementDescriptor ?? '',
      isCustom: Boolean(data?.isCustom ?? data?.IsCustom),
      suggestedStatementDescriptor: data?.suggestedStatementDescriptor ?? data?.SuggestedStatementDescriptor ?? '',
    };
  },

  updateStatementDescriptor: async (storeId, statementDescriptor) => {
    const response = await api.put(
      `/stores/${storeId}/statement-descriptor`,
      { statementDescriptor: statementDescriptor || null },
      { _storeId: storeId },
    );
    const data = response.data;
    return {
      statementDescriptor: data?.statementDescriptor ?? data?.StatementDescriptor ?? '',
      isCustom: Boolean(data?.isCustom ?? data?.IsCustom),
      suggestedStatementDescriptor: data?.suggestedStatementDescriptor ?? data?.SuggestedStatementDescriptor ?? '',
    };
  },

  updateBrandColors: async (storeId, colors) => {
    const response = await api.put(
      `/stores/${storeId}/brand-colors`,
      {
        primary: colors?.primary || null,
        accent: colors?.accent || null,
        text: colors?.text || null,
        background: colors?.background || null,
      },
      { _storeId: storeId },
    );
    return response.data ?? null;
  },

  updatePwaAppName: async (storeId, appName) => {
    await api.patch(`/stores/${storeId}/pwa-app-name`, { appName });
  },

  reorderImages: async (storeId, orderedImageIds) => {
    await api.put(`/stores/${storeId}/images/reorder`, { orderedImageIds }, { _storeId: storeId });
  },
  createBillingPortalSession: async (storeId) => {
    const response = await api.post(`/stores/${storeId}/billing-portal-session`);
    return response.data;
  },

  createCheckoutSession: async (storeId) => {
    const response = await api.post(`/stores/${storeId}/checkout-session`);
    return response.data;
  },

  getBillingStatus: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/billing-status`, { _storeId: storeId });
    return response.data;
  },

  getSmsSettings: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/sms-settings`, { _storeId: storeId });
    return response.data;
  },

  updateSmsSettings: async (storeId, { enabled }) => {
    const response = await api.put(`/stores/${storeId}/sms-settings`, { enabled }, { _storeId: storeId });
    return response.data;
  },

  cancelSubscription: async (storeId) => {
    const response = await api.post(`/stores/${storeId}/subscription/cancel`, null, { _storeId: storeId });
    return response.data;
  },

  getTeamMemberBillingImpact: async (storeId, { projectedAdditionalMember = true } = {}) => {
    const response = await api.get(`/stores/${storeId}/team-member-billing-impact`, {
      _storeId: storeId,
      params: { projectedAdditionalMember },
    });
    return response.data;
  },

  getAccessStatus: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/access-status`, { _storeId: storeId });
    return response.data;
  },

  dismissReactivateNudge: async (storeId, stage) => {
    await api.post(`/stores/${storeId}/reactivate-nudge/dismiss`, { stage }, { _storeId: storeId });
  },

  getOnboardingStatus: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/onboarding-status`, { _storeId: storeId });
    return response.data;
  },

  createOnboardingLink: async (storeId) => {
    const response = await api.post(`/stores/${storeId}/onboarding-link`, null, { _storeId: storeId });
    return response.data;
  },

  refreshAccountStatus: async (storeId) => {
    const response = await api.post(`/stores/${storeId}/refresh-account-status`, null, { _storeId: storeId });
    return response.data;
  },

  // Customer-specific endpoints
  getCustomerStoreOverview: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/overview`);
    return response.data;
  },

  // Resolve a store (incl. its id) from its slug for subdomain store hosts.
  getCustomerStoreOverviewBySlug: async (slug) => {
    const response = await api.get(`/stores/by-slug/${encodeURIComponent(slug)}/overview`);
    return response.data;
  },

  getNotificationSettings: async (storeId) => {
    const response = await api.get(`/stores/${storeId}/notification-settings`, { _storeId: storeId });
    return response.data;
  },

  updateNotificationSettings: async (storeId, settings) => {
    const response = await api.put(`/stores/${storeId}/notification-settings`, settings, { _storeId: storeId });
    return response.data;
  },

  // Get services for a store
  getServices: async (activeOnly = false) => {
    const response = await api.get(`/services?activeOnly=${activeOnly}`);
    return response.data;
  }
};

export default StoreService;
